import { useMemo, useState } from "react";
import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import {
  useStoreQuery,
  queries,
  mutations,
  type GardenWalkLog,
  type GardenWalkMonth,
  type FruitOfSpirit,
} from "@/lib/store";

/**
 * /garden-walk, the Monthly Garden Walk page.
 *
 * One fruit a month, threshold not leaderboard. Sisters log gentle walks,
 * 5 minutes or more, with an optional one-line gratitude or intention.
 * The page is designed so a sister can land here, log a walk, and be
 * back to her day in under thirty seconds. Everything else is invitation,
 * not requirement.
 */

const FRUIT_LABEL: Record<FruitOfSpirit, string> = {
  love: "Love",
  joy: "Joy",
  peace: "Peace",
  patience: "Patience",
  kindness: "Kindness",
  goodness: "Goodness",
  faithfulness: "Faithfulness",
  gentleness: "Gentleness",
  "self-control": "Self-control",
};

/**
 * Map the month's tone to actual color tokens. We keep this here rather
 * than as a CSS variable swap so the gradient blends remain crisp.
 */
function toneStyles(tone: GardenWalkMonth["tone"]) {
  switch (tone) {
    case "coral":
      return { accent: "var(--mnf-t)", soft: "rgba(192,120,74,0.14)", strong: "rgba(192,120,74,0.28)" };
    case "purple":
      return { accent: "var(--mnf-purple, #7F77DD)", soft: "rgba(127,119,221,0.14)", strong: "rgba(127,119,221,0.28)" };
    case "gold":
      return { accent: "var(--mnf-gold, #A07820)", soft: "rgba(160,120,32,0.14)", strong: "rgba(160,120,32,0.30)" };
    case "fiber":
      return { accent: "#5A8C70", soft: "rgba(168,212,184,0.18)", strong: "rgba(168,212,184,0.40)" };
    case "forest":
    default:
      return { accent: "var(--mnf-f)", soft: "rgba(30,74,53,0.10)", strong: "rgba(30,74,53,0.22)" };
  }
}

function relativeWalkTime(loggedAt: number): string {
  const diffMs = Date.now() - loggedAt;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

export default function GardenWalk() {
  const me = useStoreQuery(queries.currentUser);
  // No fallback to the seeded demo account: this page WRITES (logGardenWalk). Reading the
  // seeded demo account here would let an unresolved identity save a sister's
  // walk onto Hannah's row. See the guard below — the write is unreachable
  // until userId is a real, resolved id.
  const userId = me?.id ?? null;

  const month = useStoreQuery(queries.currentGardenWalkMonth);
  const stats = useStoreQuery(() =>
    month && userId ? queries.gardenWalkStats(userId, month.id) : null,
  );
  const monthSummary = useStoreQuery(() =>
    month ? queries.gardenWalkMonthSummary(month.id) : null,
  );
  const allLogs = useStoreQuery(() =>
    month ? queries.gardenWalkLogsForMonth(month.id) : [],
  );
  const loggedToday = useStoreQuery(() =>
    userId ? queries.hasGardenWalkLoggedToday(userId) : false,
  );

  const [composerOpen, setComposerOpen] = useState(false);
  const [minutes, setMinutes] = useState(15);
  const [reflection, setReflection] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [justLogged, setJustLogged] = useState(false);

  const tone = month ? toneStyles(month.tone) : toneStyles("forest");

  const recentFeed = useMemo(() => allLogs.slice(0, 24), [allLogs]);

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to log your Garden Walk, sister.
        </p>
      </div>
    );
  }

  if (!month || !stats || !monthSummary) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          The Garden Walk will be here next month, sister.
        </p>
      </div>
    );
  }

  const pct = Math.min(1, stats.walks / Math.max(1, stats.target));

  const handleLog = () => {
    mutations.logGardenWalk({ userId, minutes, reflection, anonymous });
    setComposerOpen(false);
    setReflection("");
    setMinutes(15);
    setAnonymous(false);
    setJustLogged(true);
    setTimeout(() => setJustLogged(false), 2400);
  };

  return (
    <div className="pb-24">
      <ScreenHeader
        eyebrow="Monthly Garden Walk"
        title={
          <>
            The month of <span style={{ fontStyle: "italic" }}>{FRUIT_LABEL[month.fruit]}</span>
          </>
        }
        subtitle={
          <>
            "{month.scripture}"
            <span style={{ color: "rgba(229,185,151,0.95)" }}> · {month.scriptureRef}</span>
          </>
        }
      />

      <div className="px-5 mt-2 space-y-4">
        {/* Hero progress card */}
        <GlassCard
          organic
          padding={20}
          data-testid="card-garden-walk-progress"
          style={{
            background: `linear-gradient(135deg, ${tone.soft} 0%, rgba(245,240,232,0.7) 100%)`,
            border: `1px solid ${tone.strong}`,
          }}
        >
          <div className="flex items-start gap-3">
            <div
              aria-hidden
              className="shrink-0 inline-flex items-center justify-center"
              style={{
                width: 56,
                height: 56,
                borderRadius: "20px 8px 20px 8px",
                background: tone.accent,
                color: "#F5F0E8",
                fontSize: 26,
              }}
            >
              {month.hero.primary}
            </div>
            <div className="flex-1 min-w-0">
              <p className="mnf-eyebrow" style={{ color: tone.accent, marginBottom: 4 }}>
                {month.title}
              </p>
              <p
                className="mnf-serif italic"
                style={{ fontSize: 15, color: "var(--mnf-fd)", lineHeight: 1.45 }}
              >
                {month.invitation}
              </p>
            </div>
          </div>

          {/* Progress arc, threshold not leaderboard */}
          <div className="mt-5">
            <div className="flex items-baseline justify-between mb-2">
              <p
                className="mnf-serif"
                style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)" }}
                data-testid="text-walks-count"
              >
                {stats.walks}
                <span
                  className="mnf-body"
                  style={{ fontSize: 13, color: "var(--mnf-tg)", fontWeight: 400, marginLeft: 6 }}
                >
                  of {stats.target} walks
                </span>
              </p>
              {stats.thresholdMet ? (
                <PillTag tone="forest">
                  <Icon name="check_circle" size={14} filled /> Bearing fruit
                </PillTag>
              ) : (
                <span className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
                  {stats.daysWalked} {stats.daysWalked === 1 ? "day" : "days"} walked
                </span>
              )}
            </div>
            <div
              className="relative"
              style={{
                height: 10,
                borderRadius: 999,
                background: "rgba(30,74,53,0.08)",
                overflow: "hidden",
              }}
              data-testid="progress-walks"
            >
              <div
                style={{
                  width: `${pct * 100}%`,
                  height: "100%",
                  background: `linear-gradient(90deg, ${tone.accent} 0%, ${tone.accent} 100%)`,
                  transition: "width 360ms ease",
                }}
              />
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <PillTag tone="neutral">
                <Icon name="schedule" size={12} /> {stats.minutes} min this month
              </PillTag>
              <PillTag tone="neutral">
                <Icon name="groups" size={12} /> {monthSummary.sisters} sisters walking
              </PillTag>
            </div>
          </div>

          {/* Today's prompt */}
          <div
            className="mt-5"
            style={{
              padding: 14,
              borderRadius: 18,
              background: "rgba(245,240,232,0.6)",
              border: "1px solid rgba(30,74,53,0.10)",
            }}
            data-testid="card-today-prompt"
          >
            <p className="mnf-eyebrow" style={{ color: tone.accent, marginBottom: 4 }}>
              Today's invitation
            </p>
            <p
              className="mnf-serif italic"
              style={{ fontSize: 14.5, color: "var(--mnf-fd)", lineHeight: 1.5 }}
            >
              {stats.todayPrompt}
            </p>
          </div>

          {/* Log CTA */}
          <div className="mt-5">
            {loggedToday && !composerOpen ? (
              <div
                className="flex items-center gap-2"
                style={{
                  padding: "12px 14px",
                  borderRadius: 14,
                  background: "rgba(168,212,184,0.22)",
                  border: "1px solid rgba(90,140,112,0.32)",
                }}
                data-testid="banner-walked-today"
              >
                <Icon name="check_circle" size={18} filled color="#5A8C70" />
                <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-fd)" }}>
                  You walked today, sister. {month.hero.secondary}
                </p>
                <button
                  onClick={() => setComposerOpen(true)}
                  className="mnf-body ml-auto"
                  style={{
                    fontSize: 12,
                    color: "var(--mnf-t)",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                  data-testid="button-log-another"
                >
                  Log another
                </button>
              </div>
            ) : !composerOpen ? (
              <Btn
                variant="primary"
                block
                onClick={() => setComposerOpen(true)}
                data-testid="button-open-walk-composer"
              >
                Log my walk
              </Btn>
            ) : null}

            {composerOpen && (
              <div
                className="space-y-3"
                style={{
                  padding: 16,
                  borderRadius: 18,
                  background: "rgba(245,240,232,0.85)",
                  border: `1px solid ${tone.strong}`,
                }}
                data-testid="composer-walk"
              >
                <div>
                  <p className="mnf-eyebrow" style={{ color: tone.accent, marginBottom: 6 }}>
                    How long, gently
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {[5, 10, 15, 20, 30, 45].map((m) => (
                      <button
                        key={m}
                        onClick={() => setMinutes(m)}
                        className="mnf-body"
                        data-testid={`button-minutes-${m}`}
                        style={{
                          height: 36,
                          padding: "0 14px",
                          borderRadius: 999,
                          background: minutes === m ? tone.accent : "transparent",
                          color: minutes === m ? "#F5F0E8" : "var(--mnf-fd)",
                          border:
                            minutes === m
                              ? `1px solid ${tone.accent}`
                              : "1px solid rgba(30,74,53,0.18)",
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {m} min
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mnf-eyebrow" style={{ color: tone.accent, marginBottom: 6 }}>
                    A line, if you have one
                  </p>
                  <textarea
                    value={reflection}
                    onChange={(e) => setReflection(e.target.value.slice(0, 140))}
                    rows={2}
                    placeholder={stats.todayPrompt}
                    className="mnf-body w-full"
                    data-testid="input-walk-reflection"
                    style={{
                      padding: 12,
                      borderRadius: 14,
                      border: "1px solid rgba(30,74,53,0.16)",
                      background: "rgba(255,255,255,0.85)",
                      fontSize: 13.5,
                      color: "var(--mnf-fd)",
                      resize: "none",
                      outline: "none",
                    }}
                  />
                  <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 4 }}>
                    Optional, sister. {140 - reflection.length} characters left.
                  </p>
                </div>

                <label
                  className="flex items-center gap-2 cursor-pointer"
                  data-testid="toggle-walk-anonymous"
                >
                  <input
                    type="checkbox"
                    checked={anonymous}
                    onChange={(e) => setAnonymous(e.target.checked)}
                    style={{ accentColor: "var(--mnf-f)" }}
                  />
                  <span className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)" }}>
                    Share quietly, hide my name from the feed
                  </span>
                </label>

                <div className="flex items-center gap-2 pt-1">
                  <Btn
                    variant="secondary"
                    onClick={() => {
                      setComposerOpen(false);
                      setReflection("");
                    }}
                    data-testid="button-cancel-walk"
                  >
                    Not now
                  </Btn>
                  <Btn variant="primary" onClick={handleLog} data-testid="button-save-walk">
                    Save my walk
                  </Btn>
                </div>
              </div>
            )}

            {justLogged && (
              <p
                className="mnf-serif italic mt-3"
                style={{ fontSize: 13, color: "var(--mnf-f)", textAlign: "center" }}
                data-testid="text-walk-confirmation"
              >
                You walked. The Lord saw it. {month.hero.secondary}
              </p>
            )}
          </div>
        </GlassCard>

        {/* Recent walks feed */}
        <SectionTitle eyebrow="The path so far" title="Recent walks" />
        {recentFeed.length === 0 ? (
          <GlassCard padding={18}>
            <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
              The path is quiet. Step out first, sister.
            </p>
          </GlassCard>
        ) : (
          <div className="space-y-2" data-testid="feed-recent-walks">
            {recentFeed.map((walk) => (
              <WalkRow key={walk.id} walk={walk} viewerId={userId} accent={tone.accent} />
            ))}
          </div>
        )}

        {/* About this challenge */}
        <SectionTitle eyebrow="A note from Kiley" title="About this walk" />
        <GlassCard padding={18}>
          <p className="mnf-body" style={{ fontSize: 13.5, color: "var(--mnf-body)", lineHeight: 1.6 }}>
            One fruit a month, drawn from Galatians 5. We walk it together, in our own
            seasons and our own paces. There is no leaderboard. A stroller walk counts.
            A walk to the mailbox counts. A pace around the kitchen with a fussy newborn
            counts. Twenty walks is the gentle threshold, not a wall.
          </p>
          <div className="flex items-center gap-2 flex-wrap mt-3">
            <Link href="/move" className="no-underline">
              <Btn variant="secondary" size="sm" data-testid="link-move">
                See today's movement
              </Btn>
            </Link>
            <Link href="/together" className="no-underline">
              <Btn variant="ghost" size="sm" data-testid="link-together">
                Other circles
              </Btn>
            </Link>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}

function WalkRow({
  walk,
  viewerId,
  accent,
}: {
  walk: GardenWalkLog;
  viewerId: string;
  accent: string;
}) {
  const profile = useStoreQuery(() =>
    walk.anonymous ? null : queries.sisterProfile(walk.userId),
  );
  const isMine = walk.userId === viewerId && !walk.anonymous;
  const displayName = walk.anonymous
    ? "A sister"
    : profile?.name ?? "A sister";
  const initials = walk.anonymous ? "·" : profile?.initials ?? "··";

  return (
    <GlassCard padding={14} data-testid={`row-walk-${walk.id}`}>
      <div className="flex items-start gap-3">
        {walk.anonymous ? (
          <div
            aria-hidden
            className="shrink-0 inline-flex items-center justify-center"
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "rgba(30,74,53,0.08)",
              color: "var(--mnf-tg)",
            }}
          >
            <Icon name="visibility_off" size={16} />
          </div>
        ) : (
          <Avatar initials={initials} size={36} ringColor={isMine ? accent : undefined} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p
              className="mnf-serif"
              style={{ fontSize: 14, fontWeight: 500, color: "var(--mnf-fd)" }}
            >
              {isMine ? "You" : displayName}
            </p>
            <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
              {walk.minutes} min · {relativeWalkTime(walk.loggedAt)}
            </span>
          </div>
          {walk.reflection && (
            <p
              className="mnf-body italic"
              style={{
                fontSize: 13,
                color: "var(--mnf-body)",
                lineHeight: 1.5,
                marginTop: 4,
              }}
            >
              "{walk.reflection}"
            </p>
          )}
        </div>
      </div>
    </GlassCard>
  );
}
